import { useState } from "react";
import { Button } from "@/components/ui/Button";
import { DatePicker } from "@/components/ui/DatePicker";
import { InfoHint } from "./InfoHint";
import type { OfficialApplicationDetail } from "../../types";

const fmt = (iso: string) => new Date(iso).toLocaleDateString("pl-PL");

/**
 * Obieg ORYGINAŁU decyzji przy wniosku o klauzulę: do koperty idzie oryginał, wraca
 * ostemplowany. Wysyłka pakietu odnotowuje „wyszedł", zakończenie („Zakończ") — „wrócił".
 * Daty da się poprawić ręcznie: faktyczne nadanie koperty bywa później niż złożenie w
 * systemie, a oryginał potrafi wrócić przed zaświadczeniem.
 */
export function DecisionOriginalCard({ app, editable, pending, error, onSave }: {
  app: OfficialApplicationDetail;
  editable: boolean;
  pending?: boolean;
  error?: string | null;
  onSave: (v: { sent_at: string | null; returned_at: string | null }) => void;
}) {
  const sent = app.decision_original_sent_at;
  const back = app.decision_original_returned_at;
  const [editing, setEditing] = useState(false);
  const [sentAt, setSentAt] = useState(sent ?? "");
  const [returnedAt, setReturnedAt] = useState(back ?? "");
  const submitted = app.status === "submitted" || app.status === "completed";
  const daysOut = sent && !back
    ? Math.max(0, Math.round((Date.now() - new Date(sent).getTime()) / 86_400_000))
    : null;
  const tone = !sent
    ? "border-[var(--color-border)] bg-[var(--color-bg-elevated)]"
    : back
      ? "border-green-300 bg-green-50 text-green-800"
      : "border-amber-300 bg-amber-50 text-amber-800";

  return (
    <div className={`border px-3 py-2 space-y-1 ${tone}`}>
      <div className="flex flex-wrap items-center gap-2">
        <b>Oryginał decyzji{app.decision_detail ? ` ${app.decision_detail.sign}` : ""}</b>
        <InfoHint text="Do koperty z pakietem idzie ORYGINAŁ decyzji — organ stawia klauzulę na nim i odsyła go razem z zaświadczeniem. Wysyłka pakietu odnotowuje, że oryginał wyszedł z firmy; „Zakończ (odpowiedź wpłynęła)” — że wrócił. Dopóki jest w urzędzie, karta świeci na bursztynowo. Daty możesz poprawić ręcznie (np. rzeczywiste nadanie koperty)." />
        <span className="flex-1">
          {!sent && (
            <>jeszcze w firmie — do koperty razem z pakietem; wysyłka odnotuje, że wyszedł.</>
          )}
          {sent && !back && (
            <>w urzędzie od <b>{fmt(sent)}</b> ({daysOut} dni) — wróci ostemplowany razem z zaświadczeniem; powrót odnotuje „Zakończ”.</>
          )}
          {sent && back && (
            <>wyszedł <b>{fmt(sent)}</b>, wrócił <b>{fmt(back)}</b>.</>
          )}
        </span>
        {editable && (submitted || sent) && !editing && (
          <Button size="sm" variant="ghost" onClick={() => {
            setSentAt(sent ?? "");
            setReturnedAt(back ?? "");
            setEditing(true);
          }}>
            Popraw daty
          </Button>
        )}
      </div>
      {editing && (
        <div className="flex flex-wrap items-end gap-3 text-[var(--color-text)]">
          <label className="block">
            <span className="section-label">Wyszedł</span>
            <DatePicker className="mt-1" value={sentAt} onChange={setSentAt} />
          </label>
          <label className="block">
            <span className="section-label">Wrócił</span>
            <DatePicker className="mt-1" value={returnedAt} min={sentAt || undefined} onChange={setReturnedAt} />
          </label>
          <div className="flex gap-1.5">
            <Button size="sm" loading={pending} onClick={() =>
              onSave({ sent_at: sentAt || null, returned_at: returnedAt || null })
            }>
              Zapisz
            </Button>
            <Button size="sm" variant="ghost" disabled={pending} onClick={() => setEditing(false)}>Anuluj</Button>
          </div>
          {error && <span className="text-xs text-red-600">{error}</span>}
        </div>
      )}
    </div>
  );
}
